import { useTranslation } from '@spcc/core';
import { cn } from '@spcc/uikit';
import {
  DEFAULT_REMEDY_KEY,
  getTechnicalDetail,
  toRemedyKey,
} from '../../domain/utils/error-remedy';

interface ScaleErrorBannerProps {
  /** `weighbridgeOps.errors.*` key (may be a raw string on legacy paths) */
  errorKey: string;
  errorParams?: Record<string, unknown>;
  className?: string;
}

/**
 * Full-width failure banner for the scale panel: what went wrong, then what the operator
 * should do about it. Replaces the truncated one-line status text — an operator who cannot
 * read the reason cannot act on it.
 */
export function ScaleErrorBanner({
  errorKey,
  errorParams,
  className,
}: ScaleErrorBannerProps) {
  const { t } = useTranslation();

  const reason = t(errorKey, { ...errorParams, defaultValue: errorKey });
  const remedy = t(toRemedyKey(errorKey), {
    ...errorParams,
    defaultValue: t(DEFAULT_REMEDY_KEY),
  });
  const technicalDetail = getTechnicalDetail(errorParams);

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-destructive dark:border-destructive/30 dark:bg-destructive/15',
        className,
      )}
    >
      <span className="mt-0.5 shrink-0 text-sm">⚠️</span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-semibold">{reason}</span>
        <span className="text-xs opacity-90">{remedy}</span>
        {technicalDetail && (
          <span
            dir="ltr"
            className="mt-0.5 break-all font-mono text-[10px] leading-snug opacity-60"
          >
            {technicalDetail}
          </span>
        )}
      </div>
    </div>
  );
}
